// client/src/pages/secure/ReviewsPage.js — reviews, testimonials and past-client touches.
//
// Every client on a closed deal gets the same review request (no review gating). The
// agent presses Send — or copies the message and sends it from their own mail — unless
// the managing broker has switched automatic sending on. A testimonial reaches the
// public site only with the client's consent AND the broker's approval.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import { Card, Empty, ErrorLine, Btn, Tabs, fmtDate, input } from '../../components/secure/territory/Shared';
import DataList from '../../components/shared/DataList';

const STATUS_LABEL = { pending: 'Waiting to send', sent: 'Sent', not_sent: 'Not sent', skipped: 'Skipped', responded: 'Responded' };
const STATUS_CLASS = { pending: 'bg-amber-100 text-amber-800', sent: 'bg-blue-100 text-blue-800', not_sent: 'bg-red-100 text-red-700',
    skipped: 'bg-slate-100 text-slate-600', responded: 'bg-green-100 text-green-800' };
const Pill = ({ s }) => <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CLASS[s] || 'bg-slate-100'}`}>{STATUS_LABEL[s] || s}</span>;
const Stars = ({ n }) => (n ? <span className="text-amber-500" aria-label={`${n} of 5`}>{'★'.repeat(n)}{'☆'.repeat(5 - n)}</span> : null);

const Tile = ({ label, value, hint }) => (
    <div className="bg-white rounded-lg shadow p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-2xl font-semibold text-slate-800">{value ?? '—'}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
);

const Overview = ({ d }) => (
    <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Requests (12 months)" value={d.requests} />
            <Tile label="Waiting to send" value={d.pending} />
            <Tile label="Response rate" value={d.response_rate == null ? '—' : `${d.response_rate}%`} hint={`${d.responded} of ${d.sent} sent`} />
            <Tile label="Average rating" value={d.average_rating ?? '—'} hint="from testimonials sent to us" />
            <Tile label="Testimonials to approve" value={d.testimonials_waiting} />
            <Tile label="Published" value={d.testimonials_published} />
            <Tile label="Past-client touches (30 days)" value={d.touches_next_30_days} />
            <Tile label="Not sent" value={d.not_sent} hint="see the reason on each request" />
        </div>
        <Card>
            <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                <li>Review links on file: {d.links_configured?.length ? d.links_configured.join(', ') : <em>none yet — the managing broker adds them under Settings</em>}.</li>
                <li>Automatic sending is <strong>{d.auto_send ? 'on' : 'off'}</strong>. {d.auto_send ? 'Due requests go out in the morning run.' : 'Requests wait for the agent to press Send.'}</li>
                {!d.sending_installed && <li>The outbound email/SMS module is not installed on this site yet, so <strong>Send</strong> prepares the message for you to copy and send from your own mailbox; press <em>I sent it</em> afterwards.</li>}
                <li>Ask every client the same way. Skipping a request needs a reason, and "might not be happy" is not one (FTC rule on reviews, 16 C.F.R. Part 465).</li>
            </ul>
        </Card>
    </div>
);

const Requests = () => {
    const api = useApiClient();
    const [status, setStatus] = useState('pending');
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const [shown, setShown] = useState(null);     // { request_id, message, testimonial_url, note }
    const load = useCallback(async () => {
        try { setRows(await api.get(`/api/reviews/requests${status ? `?status=${status}` : ''}`)); setError(''); } catch (e) { setError(e.message); }
    }, [api, status]);
    useEffect(() => { load(); }, [load]);
    const act = async (r, what, body) => {
        try {
            const out = await api.post(`/api/reviews/requests/${r.request_id}/${what}`, body || {});
            if (what === 'send' || what === 'preview') setShown({ request_id: r.request_id, ...out });
            load();
        } catch (e) { setError(e.message); }
    };
    const skip = (r) => { const reason = window.prompt('Why is this client not being asked? (recorded)'); if (reason) act(r, 'skip', { reason }); };
    return (
        <div className="space-y-3">
            <select className="p-1 border rounded text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <ErrorLine error={error} />
            {shown && (
                <Card title="The message" right={<Btn kind="secondary" onClick={() => setShown(null)}>Close</Btn>}>
                    {shown.note && <p className="text-sm mb-2 text-slate-700">{shown.note}</p>}
                    <p className="text-xs text-slate-500 mb-1">Subject: {shown.message?.subject}</p>
                    <textarea readOnly className={`${input} font-mono h-56`} value={shown.message?.text || ''} />
                    <div className="flex gap-2 mt-2">
                        <Btn kind="secondary" onClick={() => navigator.clipboard?.writeText(shown.message?.text || '')}>Copy</Btn>
                        {!shown.sent && <Btn onClick={() => { act({ request_id: shown.request_id }, 'mark-sent'); setShown(null); }}>I sent it</Btn>}
                    </div>
                </Card>
            )}
            {rows.length === 0 ? <Empty>Nothing here.</Empty> : (
                <ul className="divide-y bg-white rounded-lg shadow">
                    {rows.map((r) => (
                        <li key={r.request_id} className="p-3 flex flex-wrap gap-2 items-center justify-between">
                            <div className="text-sm">
                                <div className="font-medium">{r.first_name} {r.last_name} <Pill s={r.request_status} /></div>
                                <div className="text-slate-500">
                                    {r.street_address || `Transaction #${r.transaction_id}`} · closed {fmtDate(r.actual_close_date)} · due {fmtDate(r.due_on)}
                                    {r.agent_name ? ` · ${r.agent_name}` : ''}{r.sent_at ? ` · sent ${fmtDate(r.sent_at)}${r.auto_sent ? ' (automatic)' : ''}${r.channel === 'manual' ? ' (by hand)' : ''}` : ''}
                                </div>
                                {r.outcome_note && <div className="text-xs text-red-700">{r.outcome_note}</div>}
                            </div>
                            {!['responded', 'skipped'].includes(r.request_status) && (
                                <div className="flex gap-1">
                                    <Btn kind="secondary" onClick={() => act(r, 'preview')}>Preview</Btn>
                                    <Btn onClick={() => act(r, 'send')}>Send request</Btn>
                                    <Btn kind="secondary" onClick={() => act(r, 'mark-sent')}>I sent it</Btn>
                                    <Btn kind="danger" onClick={() => skip(r)}>Skip…</Btn>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const Testimonials = ({ mgr }) => {
    const api = useApiClient();
    const [status, setStatus] = useState('submitted');
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        try { setRows(await api.get(`/api/reviews/testimonials${status ? `?status=${status}` : ''}`)); setError(''); } catch (e) { setError(e.message); }
    }, [api, status]);
    useEffect(() => { load(); }, [load]);
    const act = async (t, what, body) => { try { await api.post(`/api/reviews/testimonials/${t.testimonial_id}/${what}`, body || {}); load(); } catch (e) { setError(e.message); } };
    return (
        <div className="space-y-3">
            <select className="p-1 border rounded text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option><option value="submitted">Waiting for approval</option><option value="approved">Published</option>
                <option value="rejected">Not published</option><option value="withdrawn">Withdrawn</option>
            </select>
            <ErrorLine error={error} />
            {rows.length === 0 ? <Empty>No testimonials here.</Empty> : rows.map((t) => (
                <Card key={t.testimonial_id}>
                    <div className="flex flex-wrap justify-between gap-2">
                        <div className="text-sm"><strong>{t.display_name}</strong> <span className="text-slate-500">({t.author_name})</span> <Stars n={t.rating} /> · {t.agent_name || '—'} · {fmtDate(t.created_at)}</div>
                        <div className="text-xs">{Number(t.consent_publish)
                            ? <span className="text-green-700">Consent to publish given {fmtDate(t.consent_at)}</span>
                            : <span className="text-red-700">No consent to publish — keep privately</span>}</div>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-slate-800">{t.body}</p>
                    {t.consent_text && <details className="mt-1 text-xs text-slate-500"><summary>Consent wording</summary>{t.consent_text}</details>}
                    {mgr && (
                        <div className="flex gap-2 mt-3">
                            {t.testimonial_status === 'submitted' && <>
                                <Btn disabled={!Number(t.consent_publish)} onClick={() => act(t, 'approve')}>Approve &amp; publish</Btn>
                                <Btn kind="secondary" onClick={() => act(t, 'reject', { note: window.prompt('Note (optional)') || '' })}>Do not publish</Btn>
                            </>}
                            {t.testimonial_status === 'approved' && <>
                                <Btn kind="secondary" onClick={() => act(t, 'unpublish')}>Take down</Btn>
                                <Btn kind="danger" onClick={() => act(t, 'unpublish', { client_withdrew: true })}>Client withdrew it</Btn>
                            </>}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
};

const Touches = () => {
    const api = useApiClient();
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    useEffect(() => { api.get('/api/reviews/touches?days=90').then(setRows).catch((e) => setError(e.message)); }, [api]);
    return (
        <div className="space-y-2">
            <p className="text-sm text-slate-600">From the <em>Closed — past-client touch plan</em> template (thank-you, check-ins, review, anniversaries for five years) and birthday touches. Complete them in the <Link className="text-indigo-700 underline" to="/dashboard/task-automation">Task Inbox</Link>; calls and texts go through the CRM buttons so Contact Compliance is asked first.</p>
            <ErrorLine error={error} />
            {rows.length === 0 ? <Empty>No touches due in the next 90 days.</Empty> : (
                <div className="bg-white rounded-lg shadow p-3"><DataList rows={rows} rowKey="task_id" csvName="past-client-touches" defaultSort={{ key: 'due_date', dir: 'asc' }}
                    filters={[{ key: 'agent_name', label: 'Agent' }]}
                    columns={[
                        { key: 'due_date', label: 'Due', type: 'date', render: (t) => <span className="whitespace-nowrap">{fmtDate(t.due_date)}</span> },
                        { key: 'title', label: 'Touch', render: (t) => <>{t.title}{t.crm_contact_id ? <> · <Link className="text-indigo-700 underline" to={`/dashboard/crm/contacts/${t.crm_contact_id}`}>card</Link></> : null}</> },
                        { key: 'agent_name', label: 'Agent', render: (t) => t.agent_name || '—' },
                    ]} /></div>
            )}
        </div>
    );
};

const Settings = () => {
    const api = useApiClient();
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    useEffect(() => { api.get('/api/reviews/settings').then(setS).catch((e) => setError(e.message)); }, [api]);
    if (!s) return <ErrorLine error={error} />;
    const set = (k, v) => setS({ ...s, [k]: v });
    const save = async () => {
        try {
            setS({ ...(await api.put('/api/reviews/settings', s)), can_edit: s.can_edit, platforms: s.platforms });
            setMsg('Saved.'); setError('');
        } catch (e) { setError(e.message); setMsg(''); }
    };
    const ro = !s.can_edit;
    return (
        <Card title="Review settings">
            <div className="grid md:grid-cols-2 gap-3 text-sm">
                {(s.platforms || []).map((p) => (
                    <label key={p.key}>{p.label} review link (https://…)
                        <input className={input} disabled={ro} value={s[p.column] || ''} onChange={(e) => set(p.column, e.target.value)} placeholder="Paste the 'write a review' link" />
                    </label>
                ))}
                <label>Ask how many days after closing
                    <input type="number" min="0" max="90" className={input} disabled={ro} value={s.request_after_days} onChange={(e) => set('request_after_days', e.target.value)} />
                </label>
                <label>Testimonial link works for (days)
                    <input type="number" min="7" max="365" className={input} disabled={ro} value={s.link_valid_days} onChange={(e) => set('link_valid_days', e.target.value)} />
                </label>
                <label className="md:col-span-2">Opening paragraph (optional — the default asks for an honest review, good or bad)
                    <textarea className={input} disabled={ro} value={s.message_intro || ''} onChange={(e) => set('message_intro', e.target.value)} />
                </label>
                <label className="flex items-center gap-2"><input type="checkbox" disabled={ro} checked={!!Number(s.auto_send)} onChange={(e) => set('auto_send', e.target.checked ? 1 : 0)} />
                    Send due requests automatically (morning run, through the outbound module and Contact Compliance)</label>
                <label className="flex items-center gap-2"><input type="checkbox" disabled={ro} checked={!!Number(s.birthday_touches)} onChange={(e) => set('birthday_touches', e.target.checked ? 1 : 0)} />
                    Birthday touch tasks (from the birthday on CRM cards)</label>
            </div>
            <p className="text-xs text-slate-500 mt-2">Do not offer anything for a review and do not ask only clients you expect to be happy. Never post or write reviews for the firm yourself.</p>
            {!ro && <div className="mt-3"><Btn onClick={save}>Save</Btn> <span className="text-sm text-green-700 ml-2">{msg}</span></div>}
            <ErrorLine error={error} />
        </Card>
    );
};

const ReviewsPage = () => {
    const api = useApiClient();
    const me = getCurrentUser();
    const mgr = canManageStaff(me);
    const [tab, setTab] = useState('overview');
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => { api.get('/api/reviews/dashboard').then(setD).catch((e) => setError(e.message)); }, [api, tab]);
    return (
        <div className="p-4 md:p-6 space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Reviews &amp; Testimonials</h1>
                <p className="text-sm text-slate-600">Review requests after closing, testimonials for the public site, and past-client touches.</p>
            </div>
            <Tabs value={tab} onChange={setTab} tabs={[['overview', 'Overview'], ['requests', 'Review requests'], ['testimonials', 'Testimonials'], ['touches', 'Past-client touches'], ['settings', 'Settings']]} />
            <ErrorLine error={error} />
            {tab === 'overview' && d && <Overview d={d} />}
            {tab === 'requests' && <Requests />}
            {tab === 'testimonials' && <Testimonials mgr={mgr} />}
            {tab === 'touches' && <Touches />}
            {tab === 'settings' && <Settings />}
        </div>
    );
};

export default ReviewsPage;
